import React from 'react';
import { Trash2 } from 'lucide-react';
import { normaliseTheme } from '../../../themes';

// Hero image panel — sits directly under Logo & brand colour so the two
// "how the top of my shop-front looks" controls live together. Panel
// styling is identical to the Logo panel — same .panel wrapper, same
// padding. Only surfaces on the Instacut default theme (locked themes
// render their own hero look).
export const HeroImagePanel = ({
  form, onChange, heroUploading, heroInputRef,
  handleHeroFile, handleHeroDelete,
}) => {
  if (normaliseTheme(form.theme) !== 'instacut-default') return null;
  return (
    <div className="panel" style={{ padding: 18, marginBottom: 14 }} data-testid="hero-panel">
      <div className="field">
        <label>Hero image
          <span className="hint"> — full-width background at the top of your shop-front &amp; booking page</span>
        </label>
        <div style={{ display: 'flex', gap: 12, alignItems: 'stretch' }}>
          {/* Preview keeps the same 16:9 aspect the landing
              page renders. When empty we show a subtle
              placeholder so the panel doesn't collapse. */}
          <div
            data-testid="hero-preview"
            style={{
              flex: '0 0 40%',
              aspectRatio: '16 / 9',
              borderRadius: 8,
              border: '1px solid var(--line)',
              backgroundImage: form.hero_image_url
                ? `linear-gradient(rgba(255,255,255,${1 - Number(form.hero_opacity ?? 1)}), rgba(255,255,255,${1 - Number(form.hero_opacity ?? 1)})), url(${form.hero_image_url})`
                : 'none',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundColor: 'var(--surface-alt, #f4f4f2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--mute)',
              fontSize: 11,
            }}
          >
            {!form.hero_image_url && 'No hero yet'}
          </div>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <label className={`drop ${heroUploading ? 'uploading' : ''}`} style={{ flex: 1 }} data-testid="hero-drop">
              {heroUploading ? 'Uploading…' : (
                <>
                  {form.hero_image_url ? 'Replace hero or ' : 'Drop a hero or '}
                  <b>browse</b><br />
                  <span style={{ fontSize: 11 }}>JPEG / PNG / WebP · max 3MB</span>
                </>
              )}
              <input
                ref={heroInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={(e) => handleHeroFile(e.target.files?.[0])}
                data-testid="hero-input"
              />
            </label>
            {form.hero_image_url && (
              <button
                type="button"
                className="btn"
                onClick={handleHeroDelete}
                disabled={heroUploading}
                data-testid="hero-delete-btn"
                style={{ alignSelf: 'flex-start', fontSize: 12 }}
              >
                <Trash2 size={13} style={{ marginRight: 6, verticalAlign: '-2px' }} /> Remove hero
              </button>
            )}
          </div>
        </div>
        <div className="hint" style={{ marginTop: 6, fontSize: 11 }}>
          Recommended 1600 × 900 px (16:9). Uploaded as-is — no cropping.
        </div>
      </div>
      <div className="field" style={{ marginBottom: 0, marginTop: 12 }}>
        <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>Hero opacity <span className="hint">how much the image shows through</span></span>
          <small style={{ fontFamily: 'var(--ff-num)', color: 'var(--mute)' }} data-testid="hero-opacity-value">
            {Math.round(Number(form.hero_opacity ?? 1) * 100)}%
          </small>
        </label>
        <input
          type="range"
          min="0.1"
          max="1"
          step="0.05"
          value={Number(form.hero_opacity ?? 1)}
          onChange={(e) => onChange({ hero_opacity: Number(e.target.value) })}
          disabled={!form.hero_image_url}
          data-testid="hero-opacity-slider"
          style={{ width: '100%' }}
        />
      </div>
    </div>
  );
};
